import { useState, useRef, useEffect } from 'react';
import useAuthStore from '../../../../stores/authStore';
import ConfirmationModal from '../../../../components/UI/ConfirmationModal';
import ModalBase from '../../../../components/UI/ModalBase/ModalBase';
import styles from './Settings.module.css';

const Settings = ({ isOpen, onClose }) => {
  const { user, updateProfile, changePassword, uploadAvatar } = useAuthStore();

  // Profile form state
  const [profileForm, setProfileForm] = useState({
    username: '',
    email: '',
    full_name: '',
    avatar_url: ''
  });

  // Password reset modal state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  // Avatar upload state
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const fileInputRef = useRef(null);

  // Form states
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [updateMessage, setUpdateMessage] = useState(null);
  const [passwordMessage, setPasswordMessage] = useState(null);

  // Initialize form with user data
  useEffect(() => {
    if (user) {
      setProfileForm({
        username: user.username || '',
        email: user.email || '',
        full_name: user.full_name || '',
        avatar_url: user.avatar_url || ''
      });
      setAvatarPreview(user.avatar_url);
    }
  }, [user]);

  // Clear messages after delay
  useEffect(() => {
    if (updateMessage) {
      const timer = setTimeout(() => setUpdateMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [updateMessage]);

  useEffect(() => {
    if (passwordMessage) {
      const timer = setTimeout(() => setPasswordMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [passwordMessage]);

  // Handle profile form changes
  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle password form changes
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle avatar file selection
  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        setUpdateMessage({ type: 'error', text: 'Avatar file must be less than 5MB' });
        return;
      }

      if (!file.type.startsWith('image/')) {
        setUpdateMessage({ type: 'error', text: 'Please select a valid image file' });
        return;
      }

      setAvatarFile(file);

      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setAvatarPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle profile update
  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setIsUpdatingProfile(true);
    setUpdateMessage(null);

    try {
      let avatarUrl = profileForm.avatar_url;

      // If there's a new avatar file, upload it first
      if (avatarFile) {
        const uploadResult = await uploadAvatar(avatarFile);

        if (uploadResult.success) {
          avatarUrl = uploadResult.avatar_url;
          setUpdateMessage({ type: 'success', text: uploadResult.message });
          setAvatarFile(null);
          // Update preview to use the server URL
          setAvatarPreview(avatarUrl);
        } else {
          setUpdateMessage({ type: 'error', text: uploadResult.message });
          setIsUpdatingProfile(false);
          return;
        }
      }

      // Update other profile fields if changed
      const hasProfileChanges = profileForm.full_name !== user?.full_name;

      if (hasProfileChanges) {
        const updatedData = {
          full_name: profileForm.full_name
        };

        const result = await updateProfile(updatedData);

        if (result.success) {
          setUpdateMessage({ type: 'success', text: 'Profile updated successfully' });
        } else {
          setUpdateMessage({ type: 'error', text: result.message });
        }
      }

    } catch (error) {
      console.error('Profile update error:', error);
      setUpdateMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  // Handle password change
  const handlePasswordSubmit = async () => {
    setIsChangingPassword(true);
    setPasswordMessage(null);

    // Validate passwords
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setPasswordMessage({ type: 'error', text: 'New passwords do not match' });
      setIsChangingPassword(false);
      return;
    }

    if (passwordForm.new_password.length < 8) {
      setPasswordMessage({ type: 'error', text: 'New password must be at least 8 characters long' });
      setIsChangingPassword(false);
      return;
    }

    try {
      // Use the authStore method
      const result = await changePassword(passwordForm.current_password, passwordForm.new_password);

      if (result.success) {
        setPasswordMessage({ type: 'success', text: result.message });
        setPasswordForm({
          current_password: '',
          new_password: '',
          confirm_password: ''
        });
        setShowPasswordModal(false);
      } else {
        setPasswordMessage({ type: 'error', text: result.message });
      }
    } catch (error) {
      console.error('Password change error:', error);
      setPasswordMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setIsChangingPassword(false);
    }
  };

  // Handle avatar click to open file dialog
  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  // Reset password modal
  const resetPasswordModal = () => {
    setPasswordForm({
      current_password: '',
      new_password: '',
      confirm_password: ''
    });
    setPasswordMessage(null);
  };

  // ModalBase handles keyboard navigation and scroll locking

  if (!user) {
    return (
      <ModalBase
        isOpen={isOpen}
        onClose={onClose}
        title="Settings"
        subtitle="Manage your account settings and preferences"
        size="large"
        variant="multi-section"
        footer={null}
      >
        <ModalBase.Content>
          <div className={styles['settings-error']}>
            <p>Unable to load user information. Please try refreshing the page.</p>
          </div>
        </ModalBase.Content>
      </ModalBase>
    );
  }

  return (
    <ModalBase
      isOpen={isOpen}
      onClose={onClose}
      title="Settings"
      subtitle="Manage your account settings and preferences"
      size="large"
      variant="multi-section"
      footer={null}
    >
      <ModalBase.Content>
        <div className={styles['settings-sections']}>
          {/* Profile Section */}
          <div className={styles['settings-section']}>
            <form onSubmit={handleProfileUpdate} className={styles['settings-profile-form']}>
              {/* Avatar Upload */}
              <div className={styles['settings-avatar-section']}>
                <label>Profile Picture</label>
                <div className={styles['settings-avatar-upload']}>
                  <div className={styles['settings-avatar-preview']} onClick={handleAvatarClick}>
                    {avatarPreview ? (
                      <img src={avatarPreview} alt="Avatar" className={styles['settings-avatar-image']} />
                    ) : (
                      <div className={styles['settings-avatar-placeholder']}>
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" fill="currentColor"/>
                          <path d="M12 14C7.58172 14 4 17.5817 4 22H20C20 17.5817 16.4183 14 12 14Z" fill="currentColor"/>
                        </svg>
                      </div>
                    )}
                    <div className={styles['settings-avatar-overlay']}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3 16.5V18.75C3 19.99 4.01 21 5.25 21H18.75C19.99 21 21 19.99 21 18.75V16.5M16.5 9L12 4.5L7.5 9M12 15V5.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarChange}
                    className={styles['settings-avatar-input']}
                  />
                  <div className={styles['settings-avatar-help']}>
                    <p>Click to upload a new picture</p>
                    <p className={styles['settings-file-requirements']}>JPG, PNG or GIF (max. 5MB)</p>
                  </div>
                </div>
              </div>

              {/* Form Fields */}
              <div className={styles['settings-form-grid']}>
                <div className={styles['settings-form-field']}>
                  <label htmlFor="username">Username</label>
                  <input
                    type="text"
                    id="username"
                    name="username"
                    value={profileForm.username}
                    onChange={handleProfileChange}
                    disabled
                    className={`${styles['settings-form-input']} ${styles.disabled}`}
                  />
                  <span className={styles['settings-field-help']}>Username cannot be changed</span>
                </div>

                <div className={styles['settings-form-field']}>
                  <label htmlFor="email">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={profileForm.email}
                    onChange={handleProfileChange}
                    disabled
                    className={`${styles['settings-form-input']} ${styles.disabled}`}
                  />
                  <span className={styles['settings-field-help']}>Email cannot be changed</span>
                </div>

                <div className={`${styles['settings-form-field']} ${styles['full-width']}`}>
                  <label htmlFor="full_name">Full Name</label>
                  <input
                    type="text"
                    id="full_name"
                    name="full_name"
                    value={profileForm.full_name}
                    onChange={handleProfileChange}
                    className={styles['settings-form-input']}
                    placeholder="Enter your full name"
                  />
                </div>
              </div>

              {/* Update Message */}
              {updateMessage && (
                <div className={`${styles['settings-message']} ${styles[`settings-message-${updateMessage.type}`]}`}>
                  {updateMessage.text}
                </div>
              )}

              {/* Form Actions */}
              <div className={styles['settings-form-actions']}>
                <button
                  type="submit"
                  disabled={isUpdatingProfile}
                  className={`${styles.btn} ${styles['btn-outline']}`}
                >
                  {isUpdatingProfile ? (
                    <>
                      <span className={styles['settings-spinner']}></span>
                      Updating...
                    </>
                  ) : (
                    'Update Profile'
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Security Section */}
          <div className={styles['settings-section']}>
            <div className={styles['settings-security-actions']}>
              <div className={styles['settings-security-item']}>
                <div className={styles['settings-security-info']}>
                  <h4>Password</h4>
                  <p>Change your password to keep your account secure</p>
                </div>
                <button
                  onClick={() => setShowPasswordModal(true)}
                  className={`${styles.btn} ${styles['btn-outline']}`}
                >
                  Change Password
                </button>
              </div>
            </div>

            {/* Password Message */}
            {passwordMessage && (
              <div className={`${styles['settings-message']} ${styles[`settings-message-${passwordMessage.type}`]}`}>
                {passwordMessage.text}
              </div>
            )}
          </div>

          {/* Account Section */}
          <div className={styles['settings-section']}>
            <div className={styles['settings-account-details']}>
              <div className={styles['settings-detail-item']}>
                <span className={styles['settings-detail-label']}>Account Created</span>
                <span className={styles['settings-detail-value']}>
                  {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                </span>
              </div>
              <div className={styles['settings-detail-item']}>
                <span className={styles['settings-detail-label']}>Last Login</span>
                <span className={styles['settings-detail-value']}>
                  {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'N/A'}
                </span>
              </div>
              <div className={styles['settings-detail-item']}>
                <span className={styles['settings-detail-label']}>Total Logins</span>
                <span className={styles['settings-detail-value']}>{user.login_count || 0}</span>
              </div>
              <div className={styles['settings-detail-item']}>
                <span className={styles['settings-detail-label']}>Account Status</span>
                <span className={`${styles['settings-detail-value']} ${styles.status} ${user.is_active ? styles.active : styles.inactive}`}>
                  {user.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className={styles['settings-detail-item']}>
                <span className={styles['settings-detail-label']}>Email Verified</span>
                <span className={`${styles['settings-detail-value']} ${styles.status} ${user.email_verified ? styles.verified : styles.unverified}`}>
                  {user.email_verified ? 'Verified' : 'Unverified'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Password Change Modal */}
        <ConfirmationModal
          isOpen={showPasswordModal}
          onClose={() => {
            setShowPasswordModal(false);
            resetPasswordModal();
          }}
          onConfirm={handlePasswordSubmit}
          title="Change Password"
          confirmText="Change Password"
          cancelText="Cancel"
          variant="info"
          isLoading={isChangingPassword}
          message={
            <div className={styles['settings-password-form']}>
              <div className={styles['settings-form-field']}>
                <label htmlFor="current_password">Current Password</label>
                <input
                  type="password"
                  id="current_password"
                  name="current_password"
                  value={passwordForm.current_password}
                  onChange={handlePasswordChange}
                  className={styles['settings-form-input']}
                  placeholder="Enter your current password"
                  required
                />
              </div>

              <div className={styles['settings-form-field']}>
                <label htmlFor="new_password">New Password</label>
                <input
                  type="password"
                  id="new_password"
                  name="new_password"
                  value={passwordForm.new_password}
                  onChange={handlePasswordChange}
                  className={styles['settings-form-input']}
                  placeholder="Enter your new password"
                  minLength={8}
                  required
                />
              </div>

              <div className={styles['settings-form-field']}>
                <label htmlFor="confirm_password">Confirm New Password</label>
                <input
                  type="password"
                  id="confirm_password"
                  name="confirm_password"
                  value={passwordForm.confirm_password}
                  onChange={handlePasswordChange}
                  className={styles['settings-form-input']}
                  placeholder="Confirm your new password"
                  minLength={8}
                  required
                />
              </div>

              {passwordMessage && (
                <div className={`${styles['settings-message']} ${styles[`settings-message-${passwordMessage.type}`]}`}>
                  {passwordMessage.text}
                </div>
              )}

              <div className={styles['settings-password-requirements']}>
                <p>Password requirements:</p>
                <ul>
                  <li>At least 8 characters long</li>
                  <li>Must contain at least one uppercase letter</li>
                  <li>Must contain at least one lowercase letter</li>
                  <li>Must contain at least one number</li>
                </ul>
              </div>
            </div>
          }
        />
      </ModalBase.Content>
    </ModalBase>
  );
};

export default Settings;